import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Autocomplete,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/reduxHooks';
import transcriptionService from '../services/transcriptionService';
import PageHeader from '../components/PageHeader';
import type { SessionHistoryItem, SessionHistoryResponse } from '../services/transcriptionService';
import SessionDetailDrawer from '../components/SessionDetailDrawer';
import ReportStatusBadge from '../components/ReportStatusBadge';
import cptExamService from '../services/cptExamService';
import type { CPTExam } from '../services/cptExamService';
import { typography } from '~/theme';

const TranscriptionHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const hardLockEnabled = useAppSelector((state) => state.report.config?.hardLockEnabled ?? false);
  const [historyData, setHistoryData] = useState<SessionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [cptFilter, setCptFilter] = useState('');
  const [cptOptions, setCptOptions] = useState<CPTExam[]>([]);
  const [cptLoading, setCptLoading] = useState(false);
  const [selectedCpt, setSelectedCpt] = useState<CPTExam | null>(null);
  const cptAbortRef = useRef<AbortController | null>(null);
  const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await transcriptionService.getSessionHistory({
        page,
        cpt_search: cptFilter || undefined,
        search: search || undefined,
      });
      setHistoryData(data);
    } catch (err) {
      console.error('Failed to fetch session history:', err);
    } finally {
      setLoading(false);
    }
  }, [page, cptFilter, search]);

  // Refetch history when navigating back to this page (e.g., from editor)
  // This ensures finalized status updates are immediately visible
  // Also handles initial mount
  useEffect(() => {
    console.log('HISTORY_PAGE_MOUNT_OR_NAVIGATION | location=%s | Refetching to sync status', location.pathname);
    fetchHistory();
  }, [location.pathname, fetchHistory]);

  // Also refetch when page regains focus (browser tab switching)
  useEffect(() => {
    const handleFocus = () => {
      console.log('HISTORY_PAGE_FOCUS | Refetching history to sync finalized status');
      fetchHistory();
    };

    window.addEventListener('focus', handleFocus);
    
    // Also listen for visibility change (more reliable for tab switching)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('HISTORY_PAGE_VISIBLE | Refetching history to sync finalized status');
        fetchHistory();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchHistory]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // CPT autocomplete: fetch options when user types >= 2 chars
  const handleCptInputChange = useCallback((_event: React.SyntheticEvent, value: string) => {
    if (cptAbortRef.current) cptAbortRef.current.abort();
    if (value.length < 2) {
      setCptOptions([]);
      return;
    }
    setCptLoading(true);
    const controller = new AbortController();
    cptAbortRef.current = controller;
    cptExamService.searchExams({ q: value, limit: 20 })
      .then((res) => {
        if (!controller.signal.aborted) setCptOptions(res.items);
      })
      .catch(() => { /* aborted or error */ })
      .finally(() => {
        if (!controller.signal.aborted) setCptLoading(false);
      });
  }, []);

  const handleViewSession = (sessionId: string) => {
    setDrawerSessionId(sessionId);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Refresh in case a report was generated
    fetchHistory();
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): 'success' | 'primary' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
      case 'corrected':
        return 'success';
      case 'in_progress':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader title="Dictation History" onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')} backLabel="Back" />

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search dictations..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button variant="outlined" size="small" onClick={handleSearch}>
          Search
        </Button>

        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={cptOptions}
          loading={cptLoading}
          value={selectedCpt}
          getOptionLabel={(opt) => `${opt.cpt_code} — ${opt.exam_name}`}
          isOptionEqualToValue={(a, b) => a.cpt_code === b.cpt_code}
          onInputChange={handleCptInputChange}
          onChange={(_e, value) => {
            setSelectedCpt(value);
            setCptFilter(value ? value.cpt_code : '');
            setPage(1);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search CPT code or exam..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={opt.cpt_code}>
              <Box>
                <Typography variant="body2" fontWeight={typography.label.fontWeight}>{opt.cpt_code}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.exam_name}</Typography>
              </Box>
            </li>
          )}
          noOptionsText="Type 2+ chars to search CPT exams"
        />

        {(search || cptFilter) && (
          <Button
            size="small"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setSelectedCpt(null);
              setCptFilter('');
              setCptOptions([]);
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
        )}

        {historyData && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {historyData.total} dictation{historyData.total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !historyData || historyData.items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {search || cptFilter
                ? 'No dictations match your filters.'
                : 'No dictation sessions found. Start by creating a new dictation.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>MRN</TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>CPT Code</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Report</TableCell>
                    <TableCell>Report Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.items.map((item: SessionHistoryItem) => (
                    <TableRow
                      key={item.session_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewSession(item.session_id)}
                    >
                      <TableCell>{formatDate(item.created_at)}</TableCell>
                      <TableCell>
                        {item.patient_mrn ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: typography.caption.fontSize }}>
                            {item.patient_mrn}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.patient_name ? (
                          <Typography variant="body2" sx={{ fontSize: typography.body2.fontSize }}>
                            {item.patient_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.cpt_code ? (
                          <Tooltip title={item.cpt_exam_name || item.cpt_code} arrow>
                            <Chip
                              label={item.cpt_exam_name ? `${item.cpt_code} — ${item.cpt_exam_name.length > 25 ? item.cpt_exam_name.substring(0, 25) + '...' : item.cpt_exam_name}` : item.cpt_code}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: typography.caption.fontSize }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDuration(item.duration_seconds)}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status ? item.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : ''}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.report_status === 'available' ? 'Available' : 'Not Generated'}
                          color={item.report_status === 'available' ? 'success' : 'default'}
                          size="small"
                          variant={item.report_status === 'available' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <ReportStatusBadge status={item.report_lifecycle_status} showIcon hardLockEnabled={hardLockEnabled} />
                      </TableCell>
                      <TableCell align="right">
                        {item.report_lifecycle_status === 'final' ? (
                          <>
                            {hardLockEnabled ? (
                              <Tooltip title="Finalized reports cannot be edited" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled
                                    sx={{ opacity: 0.4 }}
                                  >
                                    <LockIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Open & edit dictation" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/transcribe?session=${item.session_id}`);
                                  }}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Open report editor" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/report/edit?session=${item.session_id}&mode=edit`);
                                }}
                                color="secondary"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View session audio & transcript" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/sessions/${item.session_id}`);
                                }}
                              >
                                <AudiotrackIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Open & edit dictation" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/transcribe?session=${item.session_id}`);
                                }}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {item.report_status === 'available' && (
                              <Tooltip title="Open report editor" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/report/edit?session=${item.session_id}&mode=edit`);
                                  }}
                                  color="secondary"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View dictation details" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSession(item.session_id);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View session audio & transcript" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/sessions/${item.session_id}`);
                                }}
                              >
                                <AudiotrackIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {historyData.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Pagination
                  count={historyData.pages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Session detail drawer */}
      <SessionDetailDrawer
        sessionId={drawerSessionId}
        open={drawerOpen}
        onClose={handleDrawerClose}
      />
    </Box>
  );
};

export default TranscriptionHistoryPage;
